import React, { useCallback, useEffect, useRef, useState } from "react";
import { NotificationManager } from "react-notifications";
import AsyncSelect from "react-select/async";
import ModalComponent from "../../components/Modal/ModalComponent";
import ReceptionPhoto from "../../components/ReceptionPhoto/ReceptionPhoto";
import { useUserStore } from "../../contexts/userStore";
import AccessPointService from "../../services/AccessModuleService";
import CondoUserService from "../../services/CondoUserService";
import Button from "../Button/Button";
import "./OpenDoorButton.css";
import Select from "../Select/Select";
import MqttService from "../../services/MqttService";
import { useAuth } from "../../contexts/auth";
import { LockOpen } from "lucide-react";
import { debounce } from "../../utils/timer";
import { useTranslation } from "react-i18next";

const OpenDoorButton = (props) => {
	const { t } = useTranslation();
	const [optionsSelectCondoUsers, setOptionsSelectCondoUsers] = useState([]);
	const [optionsSelectAccessPoints, setOptionsSelectAccessPoints] = useState(
		[],
	);
	const [selectedCondoUser, setSelectedCondoUser] = useState();
	const [selectedAccessPoint, setSelectedAccessPoint] = useState();
	const [selectedDirection, setSelectedDirection] = useState(0);
	const [modalOpenDoor, setModalOpenDoor] = useState(false);
	const { selectedCondominio, selectedCondominioObj } = useUserStore();
	const { user } = useAuth()
	const usersAbortController = useRef(new AbortController());
	const accessPointsAbortController = useRef(new AbortController());

	const promiseUserOptions = async (inputValue) =>
		new Promise(async (resolve) => {
			usersAbortController.current?.abort();
			usersAbortController.current = new AbortController();
			const usersResponse = await CondoUserService.listCondoUsersByCondoPaginated(
				1,
				inputValue,
				user.userData.selectedOrganizationId,
				{ collumnTitle: "createdAt", orderDirection: "DESC" },
				usersAbortController.current?.signal,
				selectedCondominio,
				false,
			);
			setOptionsSelectCondoUsers(usersResponse.data?.items);

			const transformedData = usersResponse.data?.items?.map((item) => ({
				value: item.id,
				label: item.name,
			}));

			resolve(transformedData);
		});
	const promiseAccessPointsOptions = async (inputValue) =>
		new Promise(async (resolve) => {
			accessPointsAbortController.current?.abort();
			accessPointsAbortController.current = new AbortController();
			const accessResponse =
				await AccessPointService.listByCondominium(
					selectedCondominio,
					accessPointsAbortController.current?.signal,
				);
			const accessModules = [];
			for (const accessModule of accessResponse.data) {
				if (accessModule.garage || accessModule.garageIp) {
					const rfData = accessModule.garage ?? accessModule.garageIp;
					delete accessModule.garage;
					delete accessModule.garageIp;
					for (let i = 1; i <= 4; i++) {
						if (rfData[`enabled${i}`]) {
							accessModules.push({
								...accessModule,
								accessIndex: i,
								accessId: `${accessModule.id}/${i}`,
								name: rfData[`name${i}`]
							});
						}
					}
					continue;
				}
				accessModules.push(accessModule);
			}
			setOptionsSelectAccessPoints(accessModules);

			const transformedData = accessModules?.map((item) => ({
				value: `${item.id}${item.accessIndex ? `/${item.accessIndex}` : ''}`,
				accessIndex: item.accessIndex,
				label: item.name,
			}));
			const filteredData = transformedData.filter((item) =>
				!inputValue || item.label.toLowerCase().includes(inputValue.toLowerCase()),
			);
			const limitedData = filteredData.slice(0, 8);
			resolve(limitedData);
		});

	const fetchUsersDebounce = useCallback(
		debounce(promiseUserOptions, 700), [selectedCondominio]
	);
	const fetchAccessPointsDebounce = useCallback(
		debounce(promiseAccessPointsOptions, 700), [selectedCondominio]
	);

	useEffect(() => {
		const cleanup = () => {
			usersAbortController.current?.abort();
			accessPointsAbortController.current?.abort();
		}
		if (selectedCondominio) {
			promiseUserOptions();
			promiseAccessPointsOptions();
		}
		return cleanup;
	}, [selectedCondominio]);

	const handleUserSelect = (selectedOption) => {
		const userSelected = optionsSelectCondoUsers.find((user) => {
			return user.id === selectedOption.value;
		});
		setSelectedCondoUser(userSelected);
	};
	const handleAccessPointSelect = (selectedOption) => {
		const accessPointSelected = optionsSelectAccessPoints.find((access) => {
			return [`${access.id}`, access.accessId].includes(selectedOption.value) && (selectedOption.accessIndex === access.accessIndex);
		});
		setSelectedAccessPoint(accessPointSelected);
	};
	const handleConfirm = async () => {
		if (
			selectedCondominioObj?.inform_user_on_enter &&
			!props.hideUserSelection
		) {
			if (!selectedCondoUser) {
				NotificationManager.error("", "Informe um usuário", 5000, () => {
					alert("callback");
				});
				return;
			}
		}
		if (!selectedAccessPoint) {
			NotificationManager.error("", "Informe um ponto de acesso", 5000, () => {
				alert("callback");
			});
			return;
		}
		setModalOpenDoor(false);
		setSelectedCondoUser(null);
		if (props.onConfirmOpenDoor) {
			props.onConfirmOpenDoor();
		}
		//TO DO: Send open door event
		await MqttService.sendCommand({
			origin: "frontEnd",
			content: {
				id_access: selectedAccessPoint.id,
				access_index: selectedAccessPoint.accessIndex,
				id_condo_user: selectedCondoUser?.id,
				direction: selectedDirection,
				operator: user.userData.name,
			},
			action: "sendCommand",
		});
	};

	const handleCancel = () => {
		setModalOpenDoor(false);
		setSelectedCondoUser(null);
	};

	return (
		<React.Fragment>
			<Button
				className={"button-blue reception-button"}
				label={props.label || t('monitoring.openDoor')}
				onClick={() => setModalOpenDoor(true)}
				icon={<LockOpen size={15} />}
			/>
			<ModalComponent
				modalIsOpen={modalOpenDoor}
				modalText={
					props.modalText || (
						<h3 className="inline-flex items-center gap-3">
							<LockOpen size={16} />
							{t('monitoring.openDoor')}
						</h3>
					)
				}
				handleConfirm={() => handleConfirm()}
				handleCancel={() => handleCancel()}
				buttonLabel1={t('common.execute')}
			>
				<div>
					{!props.hideUserSelection && (
						<AsyncSelect
							className="basic-single reception-search-user-select"
							cacheOptions={false}
							defaultOptions
							loadOptions={fetchUsersDebounce}
							onChange={handleUserSelect}
						/>
					)}
					<AsyncSelect
						className="basic-single reception-search-user-select"
						cacheOptions={false}
						defaultOptions
						loadOptions={fetchAccessPointsDebounce}
						onChange={handleAccessPointSelect}
					/>
					<div className="reception-direction">
						<Select
							name="direction"
							id="direction"
							value={`${selectedDirection}`}
							onChange={({ target }) =>
								setSelectedDirection(Number(target.value))
							}
							required={true}
							options={[
								{ value: '0', text: t('common.entry') },
								{ value: '1', text: t('common.exit') },
							]}
						/>
					</div>
				</div>
				{!props.hideUserSelection && (
					<div
						style={{ display: "flex", alignItems: "center", height: "200px" }}
					>
						<ReceptionPhoto
							name={selectedCondoUser?.name || ""}
							urlLogo={selectedCondoUser?.signed_url_photo?.replace("[:STORAGE_ADDRESS:]", window.location.hostname) || ""}
						/>
					</div>
				)}
				{props.hideUserSelection && <div style={{ height: "150px" }}></div>}
			</ModalComponent>
		</React.Fragment>
	);
};

export default OpenDoorButton;
